/**
 * ISO 3166-1 alpha-2 country codes for the country-of-practice picker
 * (ESCLOUD-287). The profile stores the two-letter code; display names come
 * from the browser's own `Intl.DisplayNames`, so the list needs no name table.
 *
 * Named and sorted in English, matching the rest of the interface — the
 * application is not localised, and a picker whose order followed the browser
 * while every label around it stayed English would only be confusing.
 */

// prettier-ignore
const ISO_3166_1_ALPHA_2 = [
    'AD','AE','AF','AG','AI','AL','AM','AO','AQ','AR','AS','AT','AU','AW','AX','AZ',
    'BA','BB','BD','BE','BF','BG','BH','BI','BJ','BL','BM','BN','BO','BQ','BR','BS','BT','BV','BW','BY','BZ',
    'CA','CC','CD','CF','CG','CH','CI','CK','CL','CM','CN','CO','CR','CU','CV','CW','CX','CY','CZ',
    'DE','DJ','DK','DM','DO','DZ','EC','EE','EG','EH','ER','ES','ET',
    'FI','FJ','FK','FM','FO','FR','GA','GB','GD','GE','GF','GG','GH','GI','GL','GM','GN','GP','GQ','GR','GS','GT','GU','GW','GY',
    'HK','HM','HN','HR','HT','HU','ID','IE','IL','IM','IN','IO','IQ','IR','IS','IT',
    'JE','JM','JO','JP','KE','KG','KH','KI','KM','KN','KP','KR','KW','KY','KZ',
    'LA','LB','LC','LI','LK','LR','LS','LT','LU','LV','LY',
    'MA','MC','MD','ME','MF','MG','MH','MK','ML','MM','MN','MO','MP','MQ','MR','MS','MT','MU','MV','MW','MX','MY','MZ',
    'NA','NC','NE','NF','NG','NI','NL','NO','NP','NR','NU','NZ','OM',
    'PA','PE','PF','PG','PH','PK','PL','PM','PN','PR','PS','PT','PW','PY','QA',
    'RE','RO','RS','RU','RW','SA','SB','SC','SD','SE','SG','SH','SI','SJ','SK','SL','SM','SN','SO','SR','SS','ST','SV','SX','SY','SZ',
    'TC','TD','TF','TG','TH','TJ','TK','TL','TM','TN','TO','TR','TT','TV','TW','TZ',
    'UA','UG','UM','US','UY','UZ','VA','VC','VE','VG','VI','VN','VU','WF','WS','YE','YT','ZA','ZM','ZW',
] as const;

const DISPLAY_LOCALE = 'en';

export interface ICountryOption {
    code: string;
    name: string;
}

/**
 * Country options sorted by display name. `Intl.DisplayNames` can be missing a
 * code (or the API itself, in exotic environments) — the code doubles as the
 * name then, which still submits correctly.
 */
export function getCountryOptions(): ICountryOption[] {
    let displayNames: Intl.DisplayNames | null = null;
    try {
        displayNames = new Intl.DisplayNames([DISPLAY_LOCALE], { type: 'region' });
    } catch {
        displayNames = null;
    }
    return ISO_3166_1_ALPHA_2.map((code) => {
        let name: string | undefined;
        try {
            name = displayNames?.of(code);
        } catch {
            name = undefined;
        }
        return { code, name: name ?? code };
    }).sort((a, b) => a.name.localeCompare(b.name, DISPLAY_LOCALE));
}
